// ============================================================================
// TASKS CHROME — the small pieces the three columns share.
//
// The band, the honest-absence panel, the two kinds of pill, the list dot, and the one control
// type this screen needs more than any other: a button that is drawn exactly as designed, is
// reachable by keyboard, and says out loud that its backend does not exist yet.
// ============================================================================

import type { ReactNode } from 'react'
import clsx from 'clsx'

import { Recur } from './icons'
import { recurrenceWord, type DueTone } from './derive'

/**
 * THE HEADER BAND, orange/yellow (README §Shared shell: "the hue pair identifies the domain").
 *
 * Not `components/HeaderBand.tsx`: that one is a single centred row and this band is two stacked
 * rows — the title line, then the quick-add composer — at the mockup's own `18px 30px 14px`.
 * The gradient itself is a token (`--band-tasks` in index.css), so the colour still lives where
 * every other band's colour lives.
 */
export function TasksBand({ children }: { children: ReactNode }) {
  return (
    <div
      className="flex flex-none flex-col gap-[13px] px-[30px] pb-[14px] pt-[18px]"
      style={{ background: 'var(--band-tasks)' }}
    >
      {children}
    </div>
  )
}

/**
 * What a column shows when its read threw — a signed-out session, a deployment with no database,
 * a Worker that answered with a sentence. Printing that sentence is the whole point: an empty
 * board would claim "you have nothing to do", which is a different and false statement.
 */
export function Unavailable({ error }: { error: unknown }) {
  if (!error) return null
  return (
    <div className="rounded-card border border-line bg-surface px-4 py-[15px] shadow-card">
      <p className="max-w-[620px] text-[12px] leading-[1.45] text-ink-3 text-pretty">
        {error instanceof Error ? error.message : 'This could not be loaded.'}
      </p>
    </div>
  )
}

/**
 * A CONTROL WITH NO BACKEND YET.
 *
 * Copied in spirit from components/connections/parts.tsx: the button renders as designed, is
 * focusable, carries `aria-disabled` and a title naming what it is waiting for, and clicking it
 * does nothing and claims nothing. A control that silently no-ops is worse than no control.
 */
export function PendingControl({
  label,
  waitingFor,
  className,
  icon,
}: {
  label: ReactNode
  waitingFor: string
  className?: string
  icon?: ReactNode
}) {
  return (
    <button
      type="button"
      aria-disabled="true"
      title={waitingFor}
      onClick={(e) => e.preventDefault()}
      className={clsx(
        // INERT, per motion-brief §2's non-negotiable: "Disabled controls have no hover, no
        // cursor change, no transition. Not dimmed-and-still-reactive — inert." So the
        // `cursor-not-allowed` and the `transition-colors` that used to be here are both gone.
        // What the control is waiting for is still said in words, in `title` — which is where it
        // was always doing the actual work; the cursor was decoration on top of a sentence.
        'flex flex-none cursor-default items-center gap-[7px] whitespace-nowrap leading-none',
        className,
      )}
    >
      {icon}
      {label}
    </button>
  )
}

// ── Lists ───────────────────────────────────────────────────────────────────

/**
 * A list's colour, as the mockup assigns them. Three lists, three domain hues already in the
 * token file — School admin borrows School's blue, Apartment borrows Learning's teal, Errands
 * wears Tasks' own orange.
 *
 * A LIST THIS DOES NOT KNOW GETS THE NEUTRAL DOT, never a colour picked by hashing its name. A
 * generated colour would be the app inventing a property of the user's own list, and it would
 * change the day the list was renamed.
 */
const LIST_DOT: Record<string, string> = {
  'School admin': 'bg-school',
  Apartment: 'bg-learning',
  Errands: 'bg-tasks',
}

export function ListDot({ list, size = 7 }: { list: string; size?: number }) {
  return (
    <span
      style={{ width: size, height: size }}
      className={clsx('flex-none rounded-[2px]', LIST_DOT[list] ?? 'bg-dash')}
      aria-hidden
    />
  )
}

/** The row's right-hand list chip. Unfiled tasks get no chip — see TaskRow. */
export function ListChip({ list }: { list: string }) {
  return (
    <span className="flex flex-none items-center gap-[5px] whitespace-nowrap rounded-pill bg-fill-2 px-[9px] py-[3px] text-pill font-medium text-ink-2">
      <ListDot list={list} size={5} />
      {list}
    </span>
  )
}

// ── Row pills ───────────────────────────────────────────────────────────────

const DUE_TONE: Record<DueTone, string> = {
  overdue: 'bg-danger-line-2 text-danger-2',
  today: 'bg-orange-fill-2 text-orange',
  ahead: 'bg-fill-2 text-ink-2',
}

export function DuePill({ text, tone }: { text: string; tone: DueTone }) {
  return (
    <span
      className={clsx(
        'flex-none whitespace-nowrap rounded-pill px-[9px] py-[3px] text-pill font-semibold',
        DUE_TONE[tone],
      )}
    >
      {text}
    </span>
  )
}

/** 'weekly' / 'monthly'. Absent when the RRULE is one this codebase will not put a word to. */
export function RecurrenceChip({ rrule }: { rrule: string | null }) {
  const word = recurrenceWord(rrule)
  if (word === null) return null
  return (
    <span className="flex flex-none items-center gap-[5px] whitespace-nowrap rounded-pill bg-fill-2 px-[9px] py-[3px] text-pill font-medium text-ink-3">
      <Recur size={11} className="text-muted" />
      {word}
    </span>
  )
}

// ── Group furniture ─────────────────────────────────────────────────────────

/** 'TOMORROW · FRI 21' — the day header inside a grouped card, and in the Done log. */
export function DayEyebrow({ children }: { children: ReactNode }) {
  return (
    <div className="text-[9.5px] font-semibold leading-none tracking-[0.08em] text-microcopy">
      {children}
    </div>
  )
}

/** The hairline between two rows of one card. */
export function RowRule() {
  return <div className="mx-4 h-px flex-none bg-line-hair" aria-hidden />
}
